// src/prototype/enterprise/useEnterpriseRun.ts
// ============================================================
// ENTERPRISE "AS-OF" RUN — the selected scoring run, shared across the shell.
//
// The Portfolio surface's run-timeline control (Surface A · 1e) sets it; the navy sub-nav's
// "as of {run}" stamp reads it. Same localStorage + custom-event pattern as useEnterpriseMode
// so every instance stays in sync. The default (and the value when the flag is off) is the
// LATEST run — the +6-Month Actual — resolved from the spine adapter (never a hardcoded id), so
// the first composite a partner sees (hero 82.0) matches the CORE drilldown/portfolio.
// ============================================================

import { useCallback, useEffect, useState } from 'react';
import { getRuns } from '../data/adapters/enterpriseSpine';

export const ENTERPRISE_RUN_KEY = 'dva_demo_enterprise_run';
const CHANGE_EVENT = 'dva_demo_enterprise_run_change';

/** The latest run id (+6-Month Actual) — the default as-of run so the first number a partner
 *  sees is the hero's real 82.0. Adapter-derived (runs are ordered oldest→newest). */
function defaultRunId(): string {
  const runs = getRuns();
  return runs.length ? runs[runs.length - 1].run_id : '';
}

function readRunId(): string {
  const fallback = defaultRunId();
  if (typeof window === 'undefined') return fallback;
  const stored = localStorage.getItem(ENTERPRISE_RUN_KEY);
  // Guard against a stale id from an earlier seed — only honor ids the adapter still knows.
  return stored && getRuns().some((r) => r.run_id === stored) ? stored : fallback;
}

export interface EnterpriseRunApi {
  runId: string;
  setRunId: (runId: string) => void;
}

export function useEnterpriseRun(): EnterpriseRunApi {
  const [runId, setRunIdState] = useState<string>(() => readRunId());

  useEffect(() => {
    const handle = (e: Event) => setRunIdState((e as CustomEvent<string>).detail ?? readRunId());
    window.addEventListener(CHANGE_EVENT, handle as EventListener);
    return () => window.removeEventListener(CHANGE_EVENT, handle as EventListener);
  }, []);

  const setRunId = useCallback((next: string) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(ENTERPRISE_RUN_KEY, next);
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }));
    }
    setRunIdState(next);
  }, []);

  return { runId, setRunId };
}

export default useEnterpriseRun;
